import React, { ReactElement, useMemo } from 'react';
import Checkbox from './draftElements/Checkbox';
import DropdownOrMultipleSelection from './draftElements/DropdownOrMultipleSelection';
import FormInput from './draftElements/FormInput';
import Rating from './draftElements/Rating';
import Date from './draftElements/Date';
import Time from './draftElements/Time';
import SignatureInput from './draftElements/SignatureInput';
import { Box } from '@televet/kibble-ui';
import { FormQuestionType } from '@televet/shared-types';

export interface IQuestion {
  index: number;
  type?: FormQuestionType;
  prompt: string;
  defaultValue?: boolean | null;
  options?: string[];
  minValue?: number;
  maxValue: number;
  isRequired: boolean;
  hasFillInTheBlanks?: boolean;
}

export interface IFromDraftCommonProps {
  index: number;
  prompt: string;
  isRequired: boolean;
  lastIndex: number;
}

interface IQuestionProps {
  question: IQuestion;
  lastIndex: number;
}

const Question = ({ question, lastIndex }: IQuestionProps): ReactElement => {
  const { index, type, prompt, defaultValue, options, minValue, maxValue, isRequired } = question;

  const input = useMemo(() => {
    switch (type) {
      case FormQuestionType.Checkbox:
        return (
          <Checkbox
            index={index}
            prompt={prompt || ''}
            defaultValue={defaultValue || false}
            isRequired={isRequired || false}
            lastIndex={lastIndex}
          />
        );
      case FormQuestionType.MultipleSelection:
        return (
          <DropdownOrMultipleSelection
            questionType={type}
            index={index}
            prompt={prompt || ''}
            options={options || []}
            isRequired={isRequired || false}
            lastIndex={lastIndex}
          />
        );
      case FormQuestionType.TextInput:
        return (
          <FormInput
            questionType={type}
            index={index}
            prompt={prompt || ''}
            isRequired={isRequired || false}
            lastIndex={lastIndex}
          />
        );
      case FormQuestionType.MultiLineTextInput:
        return (
          <FormInput
            questionType={type}
            index={index}
            prompt={prompt || ''}
            isRequired={isRequired || false}
            lastIndex={lastIndex}
          />
        );
      case FormQuestionType.RatingInput:
        return (
          <Rating
            index={index}
            prompt={prompt || ''}
            minValue={minValue || 0}
            maxValue={maxValue || 5}
            isRequired={isRequired || false}
            lastIndex={lastIndex}
          />
        );
      case FormQuestionType.DropDown:
        return (
          <DropdownOrMultipleSelection
            questionType={type}
            index={index}
            prompt={prompt || ''}
            options={options || []}
            isRequired={isRequired || false}
            lastIndex={lastIndex}
          />
        );
      case FormQuestionType.PetPicker:
        return (
          <DropdownOrMultipleSelection
            questionType={type}
            index={index}
            prompt={prompt || ''}
            options={options || []}
            isRequired={isRequired || false}
            lastIndex={lastIndex}
          />
        );
      case FormQuestionType.DateInput:
        return <Date index={index} prompt={prompt || ''} isRequired={isRequired || false} lastIndex={lastIndex} />;
      case FormQuestionType.TimeInput:
        return <Time index={index} prompt={prompt || ''} isRequired={isRequired || false} lastIndex={lastIndex} />;
      case FormQuestionType.TextBlock:
        return (
          <FormInput questionType={type} index={index} prompt={prompt || ''} lastIndex={lastIndex} isRequired={false} />
        );
      case FormQuestionType.SignatureInput:
        return (
          <SignatureInput index={index} prompt={prompt || ''} isRequired={isRequired || false} lastIndex={lastIndex} />
        );
      default:
        return <></>;
    }
  }, [lastIndex, defaultValue, index, isRequired, maxValue, minValue, options, prompt, type]);

  return (
    <Box bg="background.subtle" p={5} borderRadius="lg">
      {input}
    </Box>
  );
};

export default Question;
